import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { Contract, ContractAttachment, Farmer } from '../../../../types';
import { getSeasonsByFarmer, createContract } from '../../../../store';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { uploadFile } from '../../../../api/file';
import { todayInputValue } from '../../../../utils/format';
import { CropSelect } from '@/components/feature/CropSelect';
import { DocumentUploadSlot, type ApplyExtracted } from '@/components/feature/DocumentUploadSlot';
import { fieldNumber, fieldString, vnDateToIso } from '@/api/aiExtraction';
import { useCrops } from '@/hooks/useCrops';
import { matchCatalogName } from '@/utils/catalogMatch';
import { ClearableInput, ClearableTextarea } from '@/components/base/ClearableInput';
import { isoAfter, isoDate, positive, validate } from '@/utils/validators';

interface CreateContractModalProps {
  htxId: string;
  farmers: Farmer[];
  onClose: () => void;
  onCreated: (contract: Contract) => void;
}

const BUSINESS_TYPE_OPTIONS: Array<{ value: 'HTX_FARMER' | 'HTX_BUYER' | 'SUPPLY'; label: string }> = [
  { value: 'HTX_FARMER', label: 'HTX - Nông hộ (bao tiêu)' },
  { value: 'HTX_BUYER', label: 'HTX - Đối tác thu mua' },
  { value: 'SUPPLY', label: 'Cung ứng/Vật tư' },
];

const digitsOnly = (value: string) => value.replace(/\D/g, '');
const formatMoneyInput = (value: string) => digitsOnly(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const parseMoneyInput = (value: string) => Number(digitsOnly(value) || 0);

/** One crop the contract covers — a contract may cover several, each with its
 * own area, quantity and price. Inputs are kept as typed strings. */
interface CropLineInput {
  crop: string;
  areaHa: string;
  outputKg: string;
  price: string;
}
const emptyLine = (): CropLineInput => ({ crop: '', areaHa: '', outputKg: '', price: '' });
const inputClass = 'w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500';

export function CreateContractModal({ htxId, farmers, onClose, onCreated }: CreateContractModalProps) {
  const { user } = useAuth();
  useModalBackHandler(true, onClose);

  const [farmerId, setFarmerId] = useState(farmers[0]?.id || '');
  const [businessType, setBusinessType] = useState<'HTX_FARMER' | 'HTX_BUYER' | 'SUPPLY'>('HTX_FARMER');
  const [templateId, setTemplateId] = useState('CONTRACT_TEMPLATE_V1');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayInputValue());
  const [expiryDate, setExpiryDate] = useState('');
  const [valueVnd, setValueVnd] = useState('');
  const [lines, setLines] = useState<CropLineInput[]>([emptyLine()]);
  const [variety, setVariety] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<ContractAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const selectedFarmer = farmers.find((f) => f.id === farmerId);
  // Not memoized — memoizing an async-cache read on farmerId alone would freeze
  // whatever was in cache on first render instead of picking up real data once
  // it loads. See web/src/pages/farmer/overview/page.tsx's note.
  const farmerSeasons = getSeasonsByFarmer(farmerId);

  const toggleSeason = (id: string) =>
    setSelectedSeasons((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingCount((n) => n + files.length);
    const next = [...attachments];
    for (const file of Array.from(files)) {
      try {
        const fileId = await uploadFile(file, 'CONTRACT_DOCUMENT', file.type);
        next.push({ fileId, type: file.type.startsWith('image/') ? 'image' : 'doc', caption: file.name });
      } catch {
        showToast(`Tải lên ${file.name} thất bại`, 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
    setAttachments(next);
    e.target.value = '';
  }

  const removeAttachment = (fileId: string) => setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));

  const crops = useCrops(htxId);
  const updateLine = (i: number, patch: Partial<CropLineInput>) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const filledLines = lines.filter((l) => l.crop.trim());
  const linesTotal =
    filledLines.length > 0 && filledLines.every((l) => l.outputKg && l.price)
      ? Math.round(filledLines.reduce((sum, l) => sum + Number(l.outputKg) * parseMoneyInput(l.price), 0))
      : undefined;

  /** Fills only the fields the contract form needs from what AI read: dates,
   * value, every crop (area, quantity, price), delivery date, and the farmer's
   * seasons for those crops. Only empty inputs are filled (the default "today"
   * effective date counts as empty) unless asked to overwrite. */
  const applyContract: ApplyExtracted = (f, { overwrite }) => {
    const fill = (current: string, next: string | undefined, set: (v: string) => void, isEmpty = !current.trim()) => {
      if (next !== undefined && next !== '' && (overwrite || isEmpty)) set(next);
    };
    fill(effectiveDate, vnDateToIso(f.effectiveDate), setEffectiveDate, !effectiveDate || effectiveDate === todayInputValue());
    fill(expiryDate, vnDateToIso(f.expiryDate), setExpiryDate);
    const value = fieldNumber(f.valueVnd);
    fill(valueVnd, value !== undefined ? formatMoneyInput(String(Math.round(value))) : undefined, setValueVnd);
    fill(expectedDeliveryDate, vnDateToIso(f.expectedDeliveryDate), setExpectedDeliveryDate);

    const catalog = (crops ?? []).map((c) => c.name);
    const read = Array.isArray(f.crops) ? (f.crops as Array<Record<string, unknown>>) : [];
    const readLines: CropLineInput[] = read
      .map((c) => {
        const name = fieldString(c.crop);
        const price = fieldNumber(c.unitPriceVnd);
        return {
          // A catalog crop when one matches; otherwise the name as read, which
          // the picker shows marked "không có trong danh mục".
          crop: (name && matchCatalogName(name, catalog)) || name || '',
          areaHa: fieldNumber(c.areaHa)?.toString() ?? '',
          outputKg: fieldNumber(c.expectedOutputKg)?.toString() ?? '',
          price: price !== undefined ? formatMoneyInput(String(Math.round(price))) : '',
        };
      })
      .filter((l) => l.crop);
    if (readLines.length > 0 && (overwrite || lines.every((l) => !l.crop.trim()))) setLines(readLines);

    // Mùa vụ liên quan: the farmer's seasons growing one of the contract's crops.
    const readCrops = readLines.map((l) => l.crop);
    const related = farmerSeasons.filter((season) => readCrops.length > 0 && matchCatalogName(season.cayTrong, readCrops)).map((season) => season.id);
    if (related.length > 0 && (overwrite || selectedSeasons.length === 0)) setSelectedSeasons(related);
  };

  // Errors follow the values: what AI filled in wrong shows red at once, an
  // empty required field once "Tạo hợp đồng" was pressed.
  const [submitted, setSubmitted] = useState(false);
  const allErrors = {
    templateId: validate(templateId, [], true, 'mã mẫu hợp đồng'),
    effectiveDate: validate(effectiveDate, [isoDate], true, 'ngày hiệu lực'),
    expiryDate: validate(expiryDate, [isoDate], true, 'ngày hết hạn') ?? isoAfter(expiryDate, effectiveDate, 'Ngày hết hạn phải sau ngày hiệu lực'),
    valueVnd: validate(valueVnd, [positive('Giá trị hợp đồng')], true, 'giá trị hợp đồng'),
    expectedDeliveryDate: expectedDeliveryDate ? isoDate(expectedDeliveryDate) : undefined,
  };
  const err = (k: keyof typeof allErrors, value: string) => (value.trim() || submitted ? allErrors[k] : undefined);
  const lineErrors = lines.map((l) => ({
    crop: !l.crop.trim() && (l.areaHa || l.outputKg || l.price) ? 'Chọn cây trồng cho dòng này' : undefined,
    areaHa: l.areaHa ? positive('Diện tích', 100_000)(l.areaHa) : undefined,
    outputKg: l.outputKg ? positive('Sản lượng', 1_000_000_000)(l.outputKg) : undefined,
    price: l.price ? positive('Đơn giá')(l.price) : undefined,
  }));
  const hasErrors = Object.values(allErrors).some(Boolean) || lineErrors.some((e) => Object.values(e).some(Boolean));

  async function handleSubmit() {
    setSubmitted(true);
    if (hasErrors) {
      showToast('Vui lòng sửa các ô viền đỏ', 'warning');
      return;
    }
    if (!selectedFarmer || !user) {
      showToast('Vui lòng chọn nông hộ', 'warning');
      return;
    }
    if (!templateId.trim()) {
      showToast('Vui lòng nhập mã mẫu hợp đồng', 'warning');
      return;
    }
    if (!effectiveDate) {
      showToast('Vui lòng chọn ngày hiệu lực', 'warning');
      return;
    }
    if (!expiryDate) {
      showToast('Vui lòng chọn ngày hết hạn', 'warning');
      return;
    }
    if (expiryDate < effectiveDate) {
      showToast('Ngày hết hạn phải sau ngày hiệu lực', 'warning');
      return;
    }
    if (!valueVnd || parseMoneyInput(valueVnd) <= 0) {
      showToast('Vui lòng nhập giá trị hợp đồng', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createContract({
        htxId,
        farmerId: selectedFarmer.id,
        farmerUserId: selectedFarmer.userId,
        htxUserId: user.id,
        templateId: templateId.trim(),
        businessType,
        counterpartyName: counterpartyName.trim() || undefined,
        effectiveDate: effectiveDate || undefined,
        expiryDate: expiryDate || undefined,
        valueVnd: valueVnd ? parseMoneyInput(valueVnd) : undefined,
        // The single-crop fields stay as the summary other screens read:
        // first crop, totals, and a unit price only when there is one crop.
        mainCrop: filledLines[0]?.crop.trim() || undefined,
        variety: variety.trim() || undefined,
        areaHa: filledLines.length > 0 && filledLines.every((l) => l.areaHa) ? filledLines.reduce((sum, l) => sum + Number(l.areaHa), 0) : undefined,
        expectedOutputKg:
          filledLines.length > 0 && filledLines.every((l) => l.outputKg) ? filledLines.reduce((sum, l) => sum + Number(l.outputKg), 0) : undefined,
        expectedUnitPriceVnd: filledLines.length === 1 && filledLines[0].price ? parseMoneyInput(filledLines[0].price) : undefined,
        cropLines: filledLines.map((l) => ({
          crop: l.crop.trim(),
          areaHa: l.areaHa ? Number(l.areaHa) : undefined,
          expectedOutputKg: l.outputKg ? Number(l.outputKg) : undefined,
          unitPriceVnd: l.price ? parseMoneyInput(l.price) : undefined,
        })),
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        seasonIds: selectedSeasons.length > 0 ? selectedSeasons : undefined,
        notes: notes.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      showToast('Đã tạo hợp đồng (bản nháp)', 'success');
      onCreated(created);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Tạo hợp đồng thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">Tạo hợp đồng (bản nháp)</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <DocumentUploadSlot
            label="Hợp đồng bao tiêu đã ký (tự điền biểu mẫu)"
            hint="Tải ảnh, PDF hoặc Word (.docx) — AI tự điền ngày hiệu lực/hết hạn, giá trị, từng cây trồng (diện tích, sản lượng, đơn giá), ngày giao và mùa vụ liên quan. Tệp được đính kèm vào hợp đồng."
            docType="OFFTAKE_CONTRACT"
            accept="image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            purpose="CONTRACT_DOCUMENT"
            onUploaded={(fileId, file) =>
              setAttachments((prev) => [...prev, { fileId, type: file.type.startsWith('image/') ? 'image' : 'doc', caption: file.name }])
            }
            onExtracted={applyContract}
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nông hộ <span className="text-red-500">*</span></label>
            <select value={farmerId} onChange={(e) => { setFarmerId(e.target.value); setSelectedSeasons([]); }} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              <option value="">— Chọn nông hộ —</option>
              {farmers.map((f) => {
                const suffix = f.code || f.phone;
                return <option key={f.id} value={f.id}>{suffix ? `${f.name} (${suffix})` : f.name}</option>;
              })}
            </select>
            {selectedFarmer && !selectedFarmer.userId && (
              <p className="mt-1 text-xs text-amber-600">
                Nông hộ chưa có tài khoản — vẫn tạo được hợp đồng. Nông hộ sẽ được thêm vào làm bên ký khi đăng ký và được gộp vào hồ sơ này.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Loại hợp đồng <span className="text-red-500">*</span></label>
            <select value={businessType} onChange={(e) => setBusinessType(e.target.value as typeof businessType)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
              {BUSINESS_TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mã mẫu hợp đồng <span className="text-red-500">*</span></label>
            <ClearableInput value={templateId} onValueChange={setTemplateId} error={err('templateId', templateId)} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="CONTRACT_TEMPLATE_V1" />
          </div>

          {businessType !== 'HTX_FARMER' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đối tác</label>
              <ClearableInput value={counterpartyName} onValueChange={setCounterpartyName} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Tên đối tác thu mua/cung ứng" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày hiệu lực <span className="text-red-500">*</span></label>
              <ClearableInput type="date" value={effectiveDate} onValueChange={setEffectiveDate} error={err('effectiveDate', effectiveDate)} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="ngày hiệu lực" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày hết hạn <span className="text-red-500">*</span></label>
              <ClearableInput type="date" value={expiryDate} onValueChange={setExpiryDate} error={err('expiryDate', expiryDate)} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="ngày hết hạn" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Giá trị hợp đồng (VNĐ) <span className="text-red-500">*</span></label>
            <ClearableInput inputMode="numeric" value={valueVnd} onValueChange={(v) => setValueVnd(formatMoneyInput(v))} error={err('valueVnd', valueVnd)} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="VD: 200.000.000" aria-label="giá trị hợp đồng" />
          </div>

          {linesTotal !== undefined && linesTotal !== parseMoneyInput(valueVnd) && (
            <p className="-mt-1 text-[11px] text-slate-500">
              Tổng theo bảng cây trồng: {linesTotal.toLocaleString('vi-VN')} đ{' '}
              <button type="button" onClick={() => setValueVnd(formatMoneyInput(String(linesTotal)))} className="font-semibold text-agrida-700 underline">
                Dùng làm giá trị hợp đồng
              </button>
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cây trồng ({filledLines.length})</label>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <CropSelect value={line.crop} htxId={htxId} invalid={Boolean(lineErrors[i]?.crop)} onChange={(name) => updateLine(i, { crop: name })} className={`${inputClass} bg-white ${lineErrors[i]?.crop ? 'border-red-400' : ''}`} />
                      {lineErrors[i]?.crop && <p className="mt-1 text-[11px] text-red-600">{lineErrors[i].crop}</p>}
                    </div>
                    {lines.length > 1 && (
                      <button type="button" onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))} aria-label="Bỏ cây trồng" className="h-9 w-9 shrink-0 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-500">
                        <i className="ri-delete-bin-line" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-0.5">Diện tích (ha)</label>
                      <ClearableInput type="number" min={0} value={line.areaHa} onValueChange={(v) => updateLine(i, { areaHa: v })} error={lineErrors[i]?.areaHa} inputClassName={inputClass} aria-label="diện tích" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-0.5">Sản lượng (kg)</label>
                      <ClearableInput type="number" min={0} value={line.outputKg} onValueChange={(v) => updateLine(i, { outputKg: v })} error={lineErrors[i]?.outputKg} inputClassName={inputClass} aria-label="sản lượng" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-slate-500 mb-0.5">Đơn giá (đ/kg)</label>
                      <ClearableInput inputMode="numeric" value={line.price} onValueChange={(v) => updateLine(i, { price: formatMoneyInput(v) })} error={lineErrors[i]?.price} inputClassName={inputClass} aria-label="đơn giá" />
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} className="h-9 w-full rounded-xl border border-dashed border-agrida-300 text-xs font-semibold text-agrida-700">
                + Thêm cây trồng
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Giống</label>
              <ClearableInput value={variety} onValueChange={setVariety} inputClassName={inputClass} aria-label="giống" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày giao hàng dự kiến</label>
              <ClearableInput type="date" value={expectedDeliveryDate} onValueChange={setExpectedDeliveryDate} error={err('expectedDeliveryDate', expectedDeliveryDate)} inputClassName={inputClass} aria-label="ngày giao" />
            </div>
          </div>

          {farmerSeasons.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mùa vụ liên quan</label>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {farmerSeasons.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={selectedSeasons.includes(s.id)} onChange={() => toggleSeason(s.id)} className="rounded border-slate-300" />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <ClearableTextarea value={notes} onValueChange={setNotes} rows={2} inputClassName="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tài liệu đính kèm</label>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-sm text-slate-700 cursor-pointer hover:bg-slate-200">
              <i className="ri-attachment-line" /> Chọn tệp
              <input type="file" multiple accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleFiles} />
            </label>
            {uploadingCount > 0 && <span className="ml-2 text-xs text-slate-400">Đang tải {uploadingCount}...</span>}
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1">
                {attachments.map((a) => (
                  <li key={a.fileId} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                    <span className="min-w-0 truncate text-slate-600">{a.caption}</span>
                    <button type="button" onClick={() => removeAttachment(a.fileId)} className="text-slate-400 hover:text-red-500"><i className="ri-close-line" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !farmerId}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {submitting ? 'Đang tạo...' : 'Tạo hợp đồng'}
          </button>
        </div>
      </div>
    </div>
  );
}
